import type { Sfx } from "./fx";

/**
 * All sound is synthesised with the Web Audio API – no audio files to download, nothing to license.
 * Effects: pop (random pitch), shots per tower type, explosion, freeze, leak, buy, upgrade, sell, fanfares.
 * Music: a soft, endlessly varying pentatonic loop (marimba + pluck + bass + brushed hats) that can be switched off.
 * The AudioContext is only created after the first user gesture (browser autoplay rules).
 */
const LS_SND = "td-snd", LS_MUS = "td-mus";
const midi = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
const rnd = (a = 0, b = 1) => a + (b - a) * Math.random();

function readFlag(key: string, def: boolean) {
  try { const v = localStorage.getItem(key); return v === null ? def : v === "1"; } catch { return def; }
}
function writeFlag(key: string, v: boolean) { try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* private mode */ } }

// C – Am – F – G, in MIDI notes (root, third, fifth)
const CHORDS: [number, number, number][] = [[48, 52, 55], [45, 48, 52], [41, 45, 48], [43, 47, 50]];
const PENTA = [0, 2, 4, 7, 9]; // C major pentatonic

export class GameAudio implements Sfx {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private musicBus!: GainNode;
  private noise!: AudioBuffer;
  private sound = readFlag(LS_SND, true);
  private music = readFlag(LS_MUS, true);
  private last = new Map<string, number>();
  private popWin = { t0: 0, n: 0 };
  private timer: number | null = null;
  private nextStep = 0;
  private step = 0;
  private intensity = 0;
  private wantMusic = false;

  get soundOn() { return this.sound; }
  get musicOn() { return this.music; }

  /** call from a click / key handler – creates and resumes the context */
  unlock() {
    if (!this.ctx) {
      try {
        const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        this.ctx = ctx;
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -14; comp.knee.value = 12; comp.ratio.value = 5; comp.attack.value = 0.004; comp.release.value = 0.18;
        this.master = ctx.createGain(); this.master.gain.value = 0.85;
        this.sfxBus = ctx.createGain(); this.sfxBus.gain.value = this.sound ? 1 : 0;
        this.musicBus = ctx.createGain(); this.musicBus.gain.value = this.music ? 0.5 : 0;
        this.sfxBus.connect(this.master); this.musicBus.connect(this.master);
        this.master.connect(comp); comp.connect(ctx.destination);
        const len = ctx.sampleRate;
        this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
        const d = this.noise.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
        document.addEventListener("visibilitychange", this.onVis);
      } catch { this.ctx = null; return; }
    }
    if (this.ctx && this.ctx.state === "suspended") void this.ctx.resume();
    if (this.wantMusic) this.startMusic();
  }

  private onVis = () => {
    const c = this.ctx;
    if (!c) return;
    if (document.visibilityState === "hidden") void c.suspend(); else void c.resume();
  };

  setSound(on: boolean) {
    this.sound = on;
    writeFlag(LS_SND, on);
    if (this.ctx) this.sfxBus.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.03);
  }
  setMusic(on: boolean) {
    this.music = on;
    writeFlag(LS_MUS, on);
    if (this.ctx) this.musicBus.gain.setTargetAtTime(on ? 0.5 : 0, this.ctx.currentTime, 0.25);
    if (on && this.wantMusic) this.startMusic();
  }
  /** the game screen asks for music while it is open */
  playMusic(on: boolean) {
    this.wantMusic = on;
    if (on) this.startMusic(); else this.stopMusic();
  }
  /** 0 = calm (between waves), 1 = a wave is running */
  setIntensity(v: number) { this.intensity = v; }

  private ok() { return !!this.ctx && this.sound && this.ctx.state === "running"; }
  private gate(key: string, minGap: number) {
    const t = this.ctx!.currentTime;
    if (t - (this.last.get(key) ?? -9) < minGap) return false;
    this.last.set(key, t);
    return true;
  }

  /* ───────────── building blocks ───────────── */
  private env(g: GainNode, t: number, peak: number, attack: number, decay: number) {
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }
  private tone(f0: number, f1: number, t: number, dur: number, type: OscillatorType, vol: number, dest: AudioNode, attack = 0.004) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    this.env(g, t, vol, attack, dur);
    o.connect(g); g.connect(dest);
    o.start(t); o.stop(t + attack + dur + 0.05);
    return o;
  }
  private burst(t: number, dur: number, vol: number, type: BiquadFilterType, f0: number, f1: number, q = 0.8, dest: AudioNode = this.sfxBus, rate = 1) {
    const ctx = this.ctx!;
    const s = ctx.createBufferSource(); s.buffer = this.noise; s.playbackRate.value = rate;
    s.loop = true;
    const f = ctx.createBiquadFilter(); f.type = type; f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx.createGain();
    this.env(g, t, vol, 0.002, dur);
    s.connect(f); f.connect(g); g.connect(dest);
    s.start(t, Math.random() * 0.8); s.stop(t + dur + 0.05);
  }

  /* ───────────── sound effects ───────────── */
  pop(pitch = 1, big = false) {
    if (!this.ok()) return;
    const ctx = this.ctx!, t = ctx.currentTime;
    if (t - this.popWin.t0 > 0.09) this.popWin = { t0: t, n: 0 };
    if (++this.popWin.n > 5) return; // a dozen balloons bursting in one frame must not clip
    const p = pitch * rnd(0.93, 1.08);
    const vol = (big ? 0.5 : 0.36) / Math.sqrt(this.popWin.n);
    this.burst(t, big ? 0.13 : 0.075, vol, "bandpass", 2600 * p, 900 * p, 0.7, this.sfxBus, rnd(0.9, 1.25));
    this.tone(430 * p, 130 * p, t, big ? 0.13 : 0.085, "sine", vol * 0.9, this.sfxBus);
    if (big) this.tone(190 * p, 70 * p, t, 0.16, "triangle", vol * 0.7, this.sfxBus);
  }
  shot(kind: string, level: number) {
    if (!this.ok()) return;
    if (!this.gate("shot" + kind, kind === "pil" ? 0.05 : 0.08)) return;
    const t = this.ctx!.currentTime, lv = 1 + (level - 1) * 0.06;
    switch (kind) {
      case "pil": // quick dart "thwip"
        this.burst(t, 0.09, 0.16, "bandpass", 2400 * lv, 900, 1.6);
        this.tone(1100 * lv, 520, t, 0.07, "triangle", 0.06, this.sfxBus);
        break;
      case "kanon": // cannon thump
        this.tone(150, 42, t, 0.22, "sine", 0.55, this.sfxBus);
        this.burst(t, 0.18, 0.32, "lowpass", 900, 120, 0.7);
        break;
      case "snigskytte": // crisp rifle crack
        this.burst(t, 0.1, 0.34, "highpass", 2200, 1200, 0.7);
        this.tone(1000, 170, t, 0.13, "sawtooth", 0.14, this.sfxBus);
        this.burst(t + 0.06, 0.35, 0.07, "bandpass", 900, 500, 0.6);
        break;
      case "trold": // magic orb
        this.tone(520, 880, t, 0.24, "triangle", 0.18, this.sfxBus, 0.02);
        this.tone(525 * 1.5, 880 * 1.5, t, 0.2, "sine", 0.1, this.sfxBus, 0.02);
        this.burst(t, 0.2, 0.05, "highpass", 4000, 6000, 0.5);
        break;
      case "is": // ice chime
        this.tone(1318, 1318, t, 0.3, "sine", 0.14, this.sfxBus);
        this.tone(1318 * 2.01, 1318 * 2.01, t, 0.18, "sine", 0.06, this.sfxBus);
        break;
      case "slynge": // rubber-band twang
        this.tone(420, 190, t, 0.1, "triangle", 0.14, this.sfxBus);
        this.burst(t + 0.05, 0.05, 0.08, "bandpass", 1500, 900, 1.2);
        break;
      case "boomerang": // spinning whoosh
        this.burst(t, 0.28, 0.14, "bandpass", 700, 1800, 2.2, this.sfxBus, 1.2);
        this.tone(300, 520, t, 0.25, "sine", 0.05, this.sfxBus, 0.03);
        break;
      case "ild": // roaring flame
        this.burst(t, 0.34, 0.26, "lowpass", 1800, 300, 0.7, this.sfxBus, 0.9);
        this.tone(110, 70, t, 0.3, "sawtooth", 0.08, this.sfxBus, 0.02);
        break;
      case "lyn": // electric zap
        this.tone(2200, 160, t, 0.16, "sawtooth", 0.13, this.sfxBus);
        this.burst(t, 0.12, 0.2, "highpass", 3200, 1800, 0.6);
        this.tone(1500, 90, t + 0.05, 0.12, "square", 0.06, this.sfxBus);
        break;
      case "vind": // gust
        this.burst(t, 0.5, 0.18, "bandpass", 350, 1100, 0.9, this.sfxBus, 0.7);
        break;
      case "net": // sticky "thwip"
        this.burst(t, 0.1, 0.14, "bandpass", 3200, 1200, 1.8);
        this.tone(1700, 600, t, 0.09, "sine", 0.06, this.sfxBus);
        break;
      case "bi": // bee buzz
        this.tone(240, 250, t, 0.16, "sawtooth", 0.06, this.sfxBus, 0.01);
        this.tone(480, 470, t, 0.14, "triangle", 0.04, this.sfxBus, 0.01);
        break;
      case "morter": // deep "thoomp"
        this.tone(110, 34, t, 0.3, "sine", 0.6, this.sfxBus);
        this.burst(t, 0.22, 0.3, "lowpass", 700, 100, 0.7);
        break;
      case "ulv": // crossbow
        this.tone(560, 230, t, 0.09, "triangle", 0.15, this.sfxBus);
        this.burst(t, 0.06, 0.22, "highpass", 2400, 1500, 0.7);
        this.tone(130, 60, t, 0.1, "sine", 0.18, this.sfxBus);
        break;
      case "sky": // rain patter
        this.burst(t, 0.45, 0.13, "highpass", 5000, 4200, 0.6, this.sfxBus, 1.4);
        this.tone(1300, 1300, t + 0.1, 0.1, "sine", 0.03, this.sfxBus);
        break;
      case "syre": // acid squirt
        this.burst(t, 0.18, 0.18, "bandpass", 1200, 2600, 1.6, this.sfxBus, 1.1);
        this.tone(300, 700, t, 0.12, "sine", 0.06, this.sfxBus);
        break;
      case "tromme": // drum
        this.tone(190, 70, t, 0.22, "sine", 0.4, this.sfxBus);
        this.burst(t, 0.05, 0.15, "bandpass", 1800, 900, 1);
        break;
      case "laser": // pew
        this.tone(1900, 240, t, 0.22, "sawtooth", 0.1, this.sfxBus);
        this.tone(1900, 260, t, 0.22, "sine", 0.1, this.sfxBus);
        break;
      case "snemand": // soft poof
        this.burst(t, 0.2, 0.2, "lowpass", 900, 250, 0.6);
        this.tone(330, 180, t, 0.12, "sine", 0.1, this.sfxBus);
        break;
      case "hjort": // long bow
        this.tone(330, 140, t, 0.13, "triangle", 0.15, this.sfxBus);
        this.burst(t + 0.02, 0.2, 0.14, "bandpass", 2800, 1100, 1.3);
        break;
      case "tudse": // fuse then thud
        this.tone(900, 1200, t, 0.1, "square", 0.04, this.sfxBus);
        this.tone(90, 40, t + 0.04, 0.25, "sine", 0.4, this.sfxBus);
        break;
      case "tid": // clock tick-tock
        this.tone(1568, 1568, t, 0.06, "square", 0.05, this.sfxBus);
        this.tone(1175, 1175, t + 0.12, 0.08, "square", 0.05, this.sfxBus);
        this.tone(2093, 2093, t + 0.04, 0.5, "sine", 0.05, this.sfxBus);
        break;
      case "raket": // rocket launch
        this.burst(t, 0.4, 0.26, "bandpass", 500, 2200, 0.9, this.sfxBus, 0.9);
        this.tone(160, 90, t, 0.25, "sawtooth", 0.07, this.sfxBus);
        break;
      case "drage": // dragon roar
        this.burst(t, 0.5, 0.3, "lowpass", 1600, 200, 0.7, this.sfxBus, 0.8);
        this.tone(95, 55, t, 0.45, "sawtooth", 0.2, this.sfxBus, 0.03);
        this.tone(140, 75, t, 0.4, "sawtooth", 0.1, this.sfxBus, 0.03);
        break;
      default:
        this.burst(t, 0.08, 0.15, "bandpass", 1800, 900, 1);
    }
  }
  explosion(size = 1) {
    if (!this.ok()) return;
    if (!this.gate("boom", 0.07)) return;
    const t = this.ctx!.currentTime, s = Math.min(1.6, 0.7 + size * 0.35);
    this.burst(t, 0.55 * s, 0.55 * s, "lowpass", 2200, 110, 0.9, this.sfxBus, 0.8);
    this.tone(95, 30, t, 0.5 * s, "sine", 0.7 * s, this.sfxBus);
    this.burst(t, 0.12, 0.18, "highpass", 2800, 1800, 0.6);
  }
  freeze() {
    if (!this.ok() || !this.gate("freeze", 0.12)) return;
    const t = this.ctx!.currentTime;
    [2400, 3150, 3900].forEach((f, i) => this.tone(f * rnd(0.98, 1.03), f * 0.98, t + i * 0.035, 0.18, "sine", 0.06, this.sfxBus));
    this.burst(t, 0.12, 0.05, "highpass", 6000, 6000, 0.5);
  }
  leak() {
    if (!this.ok() || !this.gate("leak", 0.15)) return;
    const t = this.ctx!.currentTime;
    this.tone(330, 140, t, 0.4, "sawtooth", 0.16, this.sfxBus, 0.01);
    this.tone(247, 105, t + 0.1, 0.45, "triangle", 0.18, this.sfxBus, 0.01);
  }
  buy() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    this.tone(988, 988, t, 0.09, "square", 0.07, this.sfxBus);
    this.tone(1319, 1319, t + 0.07, 0.3, "square", 0.07, this.sfxBus);
    this.tone(1319 * 2, 1319 * 2, t + 0.07, 0.2, "sine", 0.05, this.sfxBus);
    this.tone(120, 80, t, 0.1, "sine", 0.25, this.sfxBus); // placing thud
  }
  upgrade() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    [72, 76, 79, 84].forEach((m, i) => {
      this.tone(midi(m), midi(m), t + i * 0.065, 0.22, "triangle", 0.12, this.sfxBus);
      this.tone(midi(m + 12), midi(m + 12), t + i * 0.065, 0.14, "sine", 0.04, this.sfxBus);
    });
    this.burst(t + 0.2, 0.3, 0.05, "highpass", 5000, 7000, 0.5);
  }
  sell() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    this.tone(1175, 1175, t, 0.1, "square", 0.06, this.sfxBus);
    this.tone(784, 784, t + 0.08, 0.22, "square", 0.06, this.sfxBus);
  }
  correct() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    this.tone(midi(84), midi(84), t, 0.22, "sine", 0.1, this.sfxBus);
    this.tone(midi(91), midi(91), t + 0.09, 0.34, "sine", 0.1, this.sfxBus);
    this.tone(midi(96), midi(96), t + 0.09, 0.2, "sine", 0.03, this.sfxBus);
  }
  wrong() {
    if (!this.ok() || !this.gate("wrong", 0.2)) return;
    const t = this.ctx!.currentTime;
    this.tone(220, 150, t, 0.2, "triangle", 0.14, this.sfxBus, 0.01);
  }
  coin() {
    if (!this.ok() || !this.gate("coin", 0.06)) return;
    const t = this.ctx!.currentTime;
    this.tone(1568, 2093, t, 0.07, "sine", 0.04, this.sfxBus);
  }
  click() {
    if (!this.ok() || !this.gate("click", 0.03)) return;
    this.tone(700, 520, this.ctx!.currentTime, 0.035, "triangle", 0.07, this.sfxBus);
  }
  deny() {
    if (!this.ok() || !this.gate("deny", 0.15)) return;
    const t = this.ctx!.currentTime;
    this.tone(210, 160, t, 0.15, "square", 0.07, this.sfxBus);
    this.tone(190, 140, t + 0.09, 0.18, "square", 0.07, this.sfxBus);
  }
  waveStart() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    this.horn(midi(60), t, 0.18); this.horn(midi(67), t + 0.16, 0.34);
  }
  win() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    [60, 64, 67, 72, 76].forEach((m, i) => this.horn(midi(m), t + i * 0.11, i === 4 ? 0.6 : 0.14));
  }
  lose() {
    if (!this.ok()) return;
    const t = this.ctx!.currentTime;
    [67, 64, 60, 55].forEach((m, i) => this.horn(midi(m), t + i * 0.2, i === 3 ? 0.7 : 0.18, 0.5));
  }
  private horn(f: number, t: number, dur: number, vol = 1) {
    const ctx = this.ctx!;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.setValueAtTime(700, t); lp.frequency.linearRampToValueAtTime(2400, t + 0.05);
    lp.connect(this.sfxBus);
    for (const dt of [-6, 6]) { const o = this.tone(f, f, t, dur, "sawtooth", 0.07 * vol, lp, 0.02); o.detune.value = dt; }
  }

  /* ───────────── music ───────────── */
  private startMusic() {
    if (!this.ctx || this.timer !== null) return;
    this.nextStep = this.ctx.currentTime + 0.15;
    this.step = 0;
    this.timer = window.setInterval(() => this.schedule(), 90);
  }
  private stopMusic() {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
  }
  private schedule() {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state !== "running" || !this.music) { this.nextStep = Math.max(this.nextStep, ctx.currentTime + 0.05); return; }
    const dur = 60 / 104 / 2; // eighth notes at 104 bpm
    while (this.nextStep < ctx.currentTime + 0.5) {
      this.playStep(this.step, this.nextStep, dur);
      this.nextStep += dur;
      this.step++;
    }
  }
  private playStep(i: number, t: number, dur: number) {
    const bar = Math.floor(i / 8) % 4, s = i % 8, chord = CHORDS[bar]!;
    const bus = this.musicBus;
    // arpeggio (pluck)
    const arp = [0, 1, 2, 1, 2, 1, 0, 1][s]!;
    this.tone(midi(chord[arp]! + 24), midi(chord[arp]! + 24), t, dur * 1.6, "triangle", 0.1, bus, 0.003);
    // bass on the beat
    if (s === 0 || s === 4) this.tone(midi(chord[0]! - 12), midi(chord[0]! - 12), t, dur * (s === 0 ? 3.4 : 2.4), "sine", 0.26, bus, 0.01);
    // marimba melody, more active while a wave is running
    const p = 0.34 + this.intensity * 0.36;
    if ((s === 0 || s === 2 || s === 3 || s === 5 || s === 7) && Math.random() < (s === 0 ? 0.85 : p)) {
      const n = 72 + PENTA[Math.floor(Math.random() * PENTA.length)]! + (Math.random() < 0.25 ? 12 : 0);
      const f = midi(n);
      this.tone(f, f, t, dur * 2.2, "sine", 0.13, bus, 0.003);
      this.tone(f * 4, f * 4, t, dur * 0.5, "sine", 0.03, bus, 0.002);
    }
    // brushed hat on off-beats
    if (s % 2 === 1 || this.intensity > 0.5) this.burst(t, 0.05, 0.035 + this.intensity * 0.02, "highpass", 7000, 7000, 0.5, bus, 1.2);
    // soft pad at the start of each bar
    if (s === 0) for (const nn of chord) { const f = midi(nn + 12); const o = this.tone(f, f, t, dur * 7.6, "sine", 0.05, bus, 0.35); o.detune.value = rnd(-4, 4); }
  }

  dispose() {
    document.removeEventListener("visibilitychange", this.onVis);
    this.stopMusic();
    void this.ctx?.close();
    this.ctx = null;
  }
}
